import {
  ActionError,
  Button,
  ConfirmDialog,
  FormFooter,
  PageHeader,
  PageTitle,
  ReadingColumn,
  SavedIndicator,
  Skeleton,
  Surface,
  toast,
} from '@westhetics/ui'
import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import { isApiError } from '@/lib/api'
import { errorMessage } from '@/lib/errors'
import { routes } from '@/routes'
import { useProcedure, useProcedureAction, useSaveProcedure } from './api'
import { ProcedureFormBlocks } from './procedure-form'
import {
  EMPTY_PROCEDURE_FORM,
  procedureFormFrom,
  procedureInput,
  procedureServerErrors,
  validateProcedureForm,
  type ProcedureFormErrors,
  type ProcedureFormState,
} from './procedure-form-state'

/**
 * Criar e editar procedimento (docs/areas/administracao.md, "Criar / editar procedimento";
 * RN-ADM-30 a 35, 70, 73 e 77; T47). Arquétipo: cadastro. O procedimento é a oferta da clínica,
 * ligada a um ou mais procedimentos da lista da Westhetics; os documentos chegam junto. Aberto pela
 * implantação (`?from=onboarding`), volta para a etapa Procedimentos.
 */

export function ProcedurePage() {
  const { id } = useParams()
  const query = useProcedure(id)
  // O formulário nasce do procedimento carregado; outro id, outro formulário.
  return <ProcedureForm key={query.data?.id ?? 'new'} id={id} query={query} />
}

function ProcedureForm({
  id,
  query,
}: {
  id: string | undefined
  query: ReturnType<typeof useProcedure>
}) {
  const isNew = !id
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const fromOnboarding = params.get('from') === 'onboarding'
  const back = fromOnboarding ? routes.onboardingStep('procedures') : routes.admin.procedures
  const save = useSaveProcedure(id)
  const action = useProcedureAction()
  const procedure = query.data

  const [form, setForm] = useState<ProcedureFormState>(() =>
    procedure ? procedureFormFrom(procedure) : EMPTY_PROCEDURE_FORM,
  )
  const [dirty, setDirty] = useState(false)
  const [errors, setErrors] = useState<ProcedureFormErrors>({})
  const [submitted, setSubmitted] = useState(false)
  const [archiving, setArchiving] = useState(false)

  function change(next: ProcedureFormState) {
    setForm(next)
    if (submitted) setErrors(validateProcedureForm(next))
    setDirty(true)
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    setSubmitted(true)
    const found = validateProcedureForm(form)
    setErrors(found)
    if (Object.keys(found).length > 0) return
    save.mutate(procedureInput(form), {
      onSuccess: () => {
        const name = form.name.trim()
        toast(isNew ? `"${name}" cadastrado` : `"${name}" salvo — vale nos próximos documentos`)
        navigate(back)
      },
      onError: (error) => setErrors(procedureServerErrors(error)),
    })
  }

  const title = isNew ? 'Novo procedimento' : (procedure?.name ?? 'Procedimento')
  const conflict =
    save.error && isApiError(save.error) && save.error.code === 'conflict'
      ? (save.error.details.existing as { id: string; isArchived: boolean } | undefined)
      : undefined
  const hasFieldError = save.isError && Object.keys(procedureServerErrors(save.error)).length > 0

  return (
    <form onSubmit={submit} noValidate>
      <PageHeader
        LinkComponent={Link}
        crumbs={[
          fromOnboarding
            ? { label: 'Implantação', href: routes.onboardingStep('procedures') }
            : { label: 'Procedimentos', href: routes.admin.procedures },
          { label: isNew ? 'novo' : (procedure?.name ?? '…') },
        ]}
      />
      <Page>
        <ReadingColumn wide>
          <PageTitle
            title={title}
            subtitle="O nome aparece no contrato e no plano de tratamento. Curto e reconhecível funciona melhor."
          />

          {!isNew && query.isError ? (
            <LoadError
              className="mt-6"
              title="Não conseguimos abrir este procedimento"
              error={query.error}
              onRetry={() => void query.refetch()}
            />
          ) : !isNew && query.isPending ? (
            <div role="status" aria-label="Carregando" className="mt-6 flex flex-col gap-4">
              <Skeleton className="h-[220px] w-full" />
              <Skeleton className="h-[160px] w-full" />
              <Skeleton className="h-[200px] w-full" />
            </div>
          ) : (
            <div className="mt-6 flex flex-col gap-4">
              {procedure?.isPendingCatalogLink ? (
                <ActionError
                  title="Este procedimento veio do sistema antigo e ainda não está ligado à lista da Westhetics"
                  description={
                    procedure.suggestedCatalogProcedure
                      ? `Pelo nome, parece ser "${procedure.suggestedCatalogProcedure.name}". Escolha o item abaixo e confira a condição de pagamento: só depois disso ele pode entrar em documentos novos.`
                      : 'Escolha o item da lista abaixo e confira a condição de pagamento: só depois disso ele pode entrar em documentos novos.'
                  }
                />
              ) : null}

              {save.isError && !hasFieldError ? (
                <ActionError
                  title="Não foi possível salvar o procedimento"
                  description={errorMessage(save.error)}
                  actions={
                    conflict ? (
                      <Button variant="outline" size="sm" asChild>
                        <Link to={routes.admin.procedure(conflict.id)}>
                          {conflict.isArchived ? 'Ver o arquivado' : 'Abrir o existente'}
                        </Link>
                      </Button>
                    ) : undefined
                  }
                />
              ) : null}

              <ProcedureFormBlocks
                form={form}
                errors={errors}
                onChange={change}
                procedure={dirty ? undefined : procedure}
              />

              <FormFooter
                status={
                  !isNew && procedure ? (
                    <SavedIndicator
                      savedAt={new Date(procedure.updatedAt)}
                      dirty={dirty}
                      saving={save.isPending}
                    />
                  ) : null
                }
                secondary={
                  <Button variant="outline" asChild>
                    <Link to={back}>Cancelar</Link>
                  </Button>
                }
                primary={
                  <Button type="submit" loading={save.isPending} loadingLabel="Salvando…">
                    {isNew ? 'Cadastrar' : 'Salvar'}
                  </Button>
                }
              />

              {!isNew && procedure && !procedure.archivedAt ? (
                <Surface className="flex flex-wrap items-center justify-between gap-4">
                  <div className="max-w-[520px]">
                    <div className="font-display text-lead text-ink leading-[1.3] font-normal">
                      Arquivar procedimento
                    </div>
                    <p className="font-body text-secondary mt-1 text-[12px] leading-[1.6] font-light">
                      Sai do catálogo e das escolhas novas. O que já foi emitido continua intacto, e
                      ele volta a qualquer momento.
                    </p>
                  </div>
                  <Button variant="outline" onClick={() => setArchiving(true)}>
                    Arquivar
                  </Button>
                </Surface>
              ) : null}
            </div>
          )}
        </ReadingColumn>
      </Page>

      <ConfirmDialog
        open={archiving}
        onOpenChange={setArchiving}
        tone="primary"
        title={`Arquivar "${procedure?.name ?? ''}"?`}
        effect="O procedimento sai do catálogo e não pode mais ser escolhido em novos documentos. Planos e contratos que já o usam continuam intactos — nada assinado se desfaz. Ele volta a qualquer momento pelo filtro Arquivados."
        confirmLabel="Arquivar"
        loading={action.isPending}
        loadingLabel="Arquivando…"
        onConfirm={() => {
          if (!procedure) return
          action.mutate(
            { id: procedure.id, action: 'archive' },
            {
              onSuccess: () => {
                toast(`"${procedure.name}" arquivado — volta a qualquer momento`)
                navigate(routes.admin.procedures)
              },
              onError: (error) => {
                setArchiving(false)
                toast(errorMessage(error))
              },
            },
          )
        }}
      />
    </form>
  )
}
